/**
 * Memorial workspace — Appearance tab (theme, wallpaper, cover, soundtrack, atmosphere).
 */
import React, { useMemo, useState } from 'react';
import { ExternalLink, Eye, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { cn } from '@/lib/utils';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  ATMOSPHERE_FIELD_LABELS,
  MEMORIAL_ACCENT_PRESETS,
  MEMORIAL_BACKGROUND_STYLES,
  MEMORIAL_CANDLE_STYLES,
  MEMORIAL_THEME_PRESETS,
  MEMORIAL_WALLPAPER_PRESETS,
  appearancePatchPayload,
  DEFAULT_APPEARANCE_RESET,
} from '../../utils/memorialAppearance';

function PreviewSwatch({ form, backendBase }) {
  const theme = MEMORIAL_THEME_PRESETS[form.theme_preset] || MEMORIAL_THEME_PRESETS.classic;
  const accent = form.theme_accent_colour
    ? MEMORIAL_ACCENT_PRESETS[form.theme_accent_colour]
    : null;
  const cover = form.cover_photo_url
    ? resolveAssetUrl(backendBase, form.cover_photo_url)
    : null;
  return (
    <div
      className="overflow-hidden rounded-xl border border-[hsl(38_14%_86%)] shadow-sm"
      data-testid="memorial-appearance-preview"
    >
      <div
        className="relative h-16 bg-gradient-to-r from-[hsl(152_28%_16%)] to-[hsl(42_28%_92%)]"
        style={cover ? { backgroundImage: `url(${cover})`, backgroundSize: 'cover', backgroundPosition: 'center right' } : undefined}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        <p className="relative px-3 py-2 text-[10px] font-medium uppercase tracking-widest text-white/80">{theme.label}</p>
      </div>
      <div className="space-y-2 bg-[hsl(42_28%_98%)] p-3">
        <div className="flex items-center gap-2">
          <span
            className="h-8 w-8 rounded-lg border-2 border-white shadow"
            style={{ background: accent?.swatch || '#c9a961' }}
          />
          <div>
            <p className="font-serif text-sm text-[#1c1917]">Preview</p>
            <p className="text-[10px] text-muted-foreground">{theme.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MemorialAppearancePanel({
  form,
  setForm,
  photoOptions,
  publicUrl,
  backendBase,
  saving,
  saveStatus,
  onSaveAppearance,
  onResetAppearance,
}) {
  const [showAtmosphere, setShowAtmosphere] = useState(form.atmosphere_enabled);

  const bgMode = form.background_style || 'theme_default';

  const updateAtmosphere = (key, value) => {
    setForm((prev) => ({
      ...prev,
      atmosphere: { ...prev.atmosphere, [key]: value },
    }));
  };

  const saveLabel = useMemo(() => {
    if (saveStatus === 'saving') return 'Saving…';
    if (saveStatus === 'saved') return 'Saved';
    if (saveStatus === 'error') return 'Save failed — try again';
    return 'Save appearance';
  }, [saveStatus]);

  return (
    <div className="space-y-4" data-testid="memorial-appearance-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-serif text-base font-light text-[#1c1917]">Appearance</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Warm, personal styling for the public memorial page — alive with light, never cold or generic.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {publicUrl ? (
            <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <Eye className="mr-1.5 h-3.5 w-3.5" />
                Preview public memorial
              </a>
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" className="rounded-full" disabled title="Publish the memorial to preview">
              <Eye className="mr-1.5 h-3.5 w-3.5" />
              Preview (publish first)
            </Button>
          )}
        </div>
      </div>

      <PreviewSwatch form={form} backendBase={backendBase} />

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5">
        <h4 className="text-sm font-medium text-[#1c1917]">Memorial theme</h4>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Object.values(MEMORIAL_THEME_PRESETS).map((theme) => (
            <button
              key={theme.id}
              type="button"
              onClick={() => setForm((p) => ({ ...p, theme_preset: theme.id }))}
              className={cn(
                'rounded-xl border px-3 py-2.5 text-left transition',
                form.theme_preset === theme.id
                  ? 'border-[#8b7355] bg-[#c9a961]/10 ring-1 ring-[#c9a961]/30'
                  : 'border-[hsl(38_14%_86%)] hover:border-[#c9a961]/40',
              )}
            >
              <span className="text-sm font-medium">{theme.label}</span>
              <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground">{theme.description}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5">
        <h4 className="text-sm font-medium text-[#1c1917]">Accent colour</h4>
        <p className="mt-1 text-[11px] text-muted-foreground">Used for headings, buttons, dividers and candle accents.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, theme_accent_colour: '' }))}
            className={cn(
              'rounded-full border px-3 py-1 text-xs',
              !form.theme_accent_colour ? 'border-[#8b7355] bg-[#c9a961]/10' : 'border-[hsl(38_14%_86%)]',
            )}
          >
            Theme default
          </button>
          {Object.values(MEMORIAL_ACCENT_PRESETS).map((accent) => (
            <button
              key={accent.id}
              type="button"
              title={accent.label}
              onClick={() => setForm((p) => ({ ...p, theme_accent_colour: accent.id }))}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                form.theme_accent_colour === accent.id ? 'border-[#8b7355] bg-[#c9a961]/10' : 'border-[hsl(38_14%_86%)]',
              )}
            >
              <span className="h-3.5 w-3.5 rounded-full border border-white/50" style={{ background: accent.swatch }} />
              {accent.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5 space-y-4">
        <h4 className="text-sm font-medium text-[#1c1917]">Background &amp; wallpaper</h4>
        <div className="space-y-2">
          <Label>Background style</Label>
          <Select
            value={bgMode}
            onValueChange={(v) => setForm((p) => ({ ...p, background_style: v }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.values(MEMORIAL_BACKGROUND_STYLES).map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {bgMode === 'preset' ? (
          <div className="space-y-2">
            <Label>Preset background</Label>
            <Select
              value={form.wallpaper_preset || '__none__'}
              onValueChange={(v) => setForm((p) => ({ ...p, wallpaper_preset: v === '__none__' ? '' : v }))}
            >
              <SelectTrigger><SelectValue placeholder="Choose preset" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {Object.values(MEMORIAL_WALLPAPER_PRESETS).map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {bgMode === 'custom_image' ? (
          <div className="space-y-2">
            <Label>Wallpaper from archive</Label>
            <Select
              value={form.wallpaper_url || '__none__'}
              onValueChange={(v) => setForm((p) => ({ ...p, wallpaper_url: v === '__none__' ? '' : v }))}
            >
              <SelectTrigger><SelectValue placeholder="Subtle background image" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None — theme gradient</SelectItem>
                {photoOptions.map((o) => (
                  <SelectItem key={`wall-${o.value}`} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">Wallpaper appears softly behind the page with a readability overlay.</p>
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5 space-y-4">
        <h4 className="text-sm font-medium text-[#1c1917]">Cover &amp; portrait</h4>
        <p className="text-[11px] text-muted-foreground">
          Cover is a wide banner only — never stretched from the portrait. Portrait stays in its own framed card.
        </p>
        <div className="space-y-2">
          <Label>Cover / banner image</Label>
          <Select value={form.cover_photo_url || '__none__'} onValueChange={(v) => setForm((p) => ({ ...p, cover_photo_url: v === '__none__' ? '' : v }))}>
            <SelectTrigger><SelectValue placeholder="Theme fallback" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Theme fallback (no cover photo)</SelectItem>
              {photoOptions.map((o) => (
                <SelectItem key={`cover-${o.value}`} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Portrait image</Label>
          <Select value={form.portrait_photo_url || '__none__'} onValueChange={(v) => setForm((p) => ({ ...p, portrait_photo_url: v === '__none__' ? '' : v }))}>
            <SelectTrigger><SelectValue placeholder="Choose portrait" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Initials placeholder</SelectItem>
              {photoOptions.map((o) => (
                <SelectItem key={`portrait-${o.value}`} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5 space-y-4">
        <h4 className="text-sm font-medium text-[#1c1917]">Candle &amp; light style</h4>
        <Select value={form.candle_style || 'classic'} onValueChange={(v) => setForm((p) => ({ ...p, candle_style: v }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.values(MEMORIAL_CANDLE_STYLES).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5 space-y-2">
        <p className="text-[11px] text-muted-foreground">
          Memory Soundtrack and song links are managed in the <strong>Music &amp; Video</strong> workspace tab.
        </p>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-medium text-[#1c1917]">Remembered through the senses</h4>
            <p className="mt-1 text-[11px] text-muted-foreground">Optional, poetic details — what they loved.</p>
          </div>
          <Switch
            checked={showAtmosphere}
            onCheckedChange={(v) => {
              setShowAtmosphere(v);
              setForm((p) => ({ ...p, atmosphere_enabled: v }));
            }}
          />
        </div>
        {showAtmosphere ? (
          <div className="space-y-3 border-t border-border/30 pt-3">
            {Object.entries(ATMOSPHERE_FIELD_LABELS).map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`atm-${key}`}>{label}</Label>
                {key === 'personal_note' ? (
                  <Textarea
                    id={`atm-${key}`}
                    value={form.atmosphere?.[key] || ''}
                    onChange={(e) => updateAtmosphere(key, e.target.value)}
                    rows={2}
                    className="resize-none"
                  />
                ) : (
                  <Input
                    id={`atm-${key}`}
                    value={form.atmosphere?.[key] || ''}
                    onChange={(e) => updateAtmosphere(key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" className="rounded-full" disabled={saving} onClick={onSaveAppearance} data-testid="memorial-save-appearance">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {saveLabel}
        </Button>
        <Button type="button" variant="outline" className="rounded-full" disabled={saving} onClick={onResetAppearance}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to default
        </Button>
        {publicUrl ? (
          <Button type="button" variant="ghost" size="sm" className="rounded-full" asChild>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Open public page
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default MemorialAppearancePanel;
